/**
 * `.adam` zip package — Curva-shaped layout for Adam projects.
 *
 * Layout:
 * ```
 * project.json
 * metadata.json
 * assets/
 * assets/audio/
 * assets/audio/manifest.json   # SampleAssetMeta[] (imported samples only)
 * assets/audio/{id}.wav
 * previews/
 * ```
 *
 * Lives under apps/audio-lab (no Curva `src/io` imports). Uses fflate like `.curva`.
 */
import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import type { SampleAssetMeta, SampleFolderMeta } from "../../../../src/lib/audio";
import {
  ADAM_FORMAT,
  ADAM_METADATA_JSON_PATH,
  ADAM_PACKAGE_VERSION,
  ADAM_PROJECT_JSON_PATH,
} from "./fileTypes";
import {
  buildLabProject,
  parseLabProject,
  type LabProjectFile,
  type LabProjectSnapshot,
} from "./projectFile";
import { decodeWavToMono, encodeWavMono16 } from "./wavCodec";

export const ADAM_AUDIO_MANIFEST_PATH = "assets/audio/manifest.json";
export const ADAM_AUDIO_FOLDERS_PATH = "assets/audio/folders.json";

export type AdamMetadataJson = {
  format: typeof ADAM_FORMAT;
  version: typeof ADAM_PACKAGE_VERSION;
  savedAt: string;
  generator: "adam";
};

/** Wrapper inside `project.json`. */
export type AdamProjectDocumentJson = {
  format: typeof ADAM_FORMAT;
  version: typeof ADAM_PACKAGE_VERSION;
  project: LabProjectFile;
};

export type AdamPackedSample = {
  meta: SampleAssetMeta;
  pcm: Float32Array;
};

export type AdamPackage = {
  snapshot: LabProjectSnapshot;
  metadata: AdamMetadataJson;
  /** Raw project file as stored (useful for round-trips). */
  project: LabProjectFile;
  /** Imported samples extracted from `assets/audio/` (factory cycles omitted). */
  samples: AdamPackedSample[];
  /** Sample library folders referenced by exported samples. */
  folders: SampleFolderMeta[];
};

export type SerializeAdamPackageOptions = {
  /** Optional Finder/Explorer preview (PNG). */
  thumbnailPng?: Uint8Array;
  /** Imported samples to embed under `assets/audio/`. */
  samples?: AdamPackedSample[];
  /** Folder metas so sample `folderId`s round-trip. */
  folders?: SampleFolderMeta[];
};

function stableJsonStringify(value: unknown): string {
  return `${JSON.stringify(sortJson(value))}\n`;
}

function sortJson(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortJson);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nested]) => [key, sortJson(nested)]),
    );
  }
  return value;
}

export function toAdamProjectDocumentJson(project: LabProjectFile): AdamProjectDocumentJson {
  return {
    format: ADAM_FORMAT,
    version: ADAM_PACKAGE_VERSION,
    project,
  };
}

export function parseAdamProjectDocumentJson(data: unknown): LabProjectSnapshot {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid .adam project.json");
  }
  const obj = data as Record<string, unknown>;
  if (obj.format !== ADAM_FORMAT) {
    throw new Error(`Expected format "${ADAM_FORMAT}", got ${String(obj.format)}`);
  }
  if (obj.version !== ADAM_PACKAGE_VERSION) {
    throw new Error(`Unsupported .adam package version: ${String(obj.version)}`);
  }
  if (!obj.project || typeof obj.project !== "object") {
    throw new Error(".adam project.json missing project");
  }
  return parseLabProject(obj.project);
}

function audioAssetPath(sampleId: string): string {
  return `assets/audio/${sampleId}.wav`;
}

/** Pack a live snapshot into `.adam` zip bytes. */
export function serializeAdamPackage(
  snapshot: LabProjectSnapshot,
  options: SerializeAdamPackageOptions = {},
): Uint8Array {
  const project = buildLabProject(snapshot);
  const metadata: AdamMetadataJson = {
    format: ADAM_FORMAT,
    version: ADAM_PACKAGE_VERSION,
    savedAt: new Date().toISOString(),
    generator: "adam",
  };

  const samples = options.samples ?? [];
  const folders = options.folders ?? [];
  const manifest: SampleAssetMeta[] = samples.map(s => s.meta);

  const entries: Record<string, Uint8Array> = {
    [ADAM_PROJECT_JSON_PATH]: strToU8(stableJsonStringify(toAdamProjectDocumentJson(project))),
    [ADAM_METADATA_JSON_PATH]: strToU8(stableJsonStringify(metadata)),
    "assets/": new Uint8Array(),
    "assets/audio/": new Uint8Array(),
    "previews/": new Uint8Array(),
  };

  if (manifest.length > 0) {
    entries[ADAM_AUDIO_MANIFEST_PATH] = strToU8(stableJsonStringify(manifest));
  }
  if (folders.length > 0) {
    entries[ADAM_AUDIO_FOLDERS_PATH] = strToU8(stableJsonStringify(folders));
  }
  for (const sample of samples) {
    entries[audioAssetPath(sample.meta.id)] = encodeWavMono16(
      sample.pcm,
      sample.meta.sampleRate,
    );
  }

  if (options.thumbnailPng && options.thumbnailPng.byteLength > 0) {
    entries["previews/thumbnail.png"] = options.thumbnailPng;
  }

  return zipSync(entries, { level: 6 });
}

function extractSamples(entries: Record<string, Uint8Array>): AdamPackedSample[] {
  const out: AdamPackedSample[] = [];
  const manifestEntry = entries[ADAM_AUDIO_MANIFEST_PATH];
  let metas: SampleAssetMeta[] = [];
  if (manifestEntry) {
    try {
      const parsed = JSON.parse(strFromU8(manifestEntry));
      if (Array.isArray(parsed)) {
        metas = parsed as SampleAssetMeta[];
      }
    } catch {
      metas = [];
    }
  }

  if (metas.length === 0) {
    // Fallback: any `assets/audio/*.wav` without manifest.
    for (const key of Object.keys(entries)) {
      const match = /^assets\/audio\/(.+)\.wav$/.exec(key);
      if (!match || match[1] === "manifest") {
        continue;
      }
      const id = match[1]!;
      try {
        const decoded = decodeWavToMono(entries[key]!);
        out.push({
          meta: {
            id,
            name: id,
            sampleRate: decoded.sampleRate,
            channels: 1,
            lengthFrames: decoded.pcm.length,
            source: "import",
            createdAt: new Date().toISOString(),
          },
          pcm: decoded.pcm,
        });
      } catch {
        // skip corrupt
      }
    }
    return out;
  }

  for (const meta of metas) {
    if (!meta?.id) {
      continue;
    }
    const wav = entries[audioAssetPath(meta.id)];
    if (!wav) {
      continue;
    }
    try {
      const decoded = decodeWavToMono(wav);
      out.push({
        meta: {
          ...meta,
          sampleRate: meta.sampleRate || decoded.sampleRate,
          lengthFrames: meta.lengthFrames || decoded.pcm.length,
          channels: 1,
        },
        pcm: decoded.pcm,
      });
    } catch {
      // skip corrupt
    }
  }
  return out;
}

function extractFolders(entries: Record<string, Uint8Array>): SampleFolderMeta[] {
  const foldersEntry = entries[ADAM_AUDIO_FOLDERS_PATH];
  if (!foldersEntry) {
    return [];
  }
  try {
    const parsed = JSON.parse(strFromU8(foldersEntry));
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (f): f is SampleFolderMeta =>
        Boolean(f && typeof f === "object" && typeof (f as SampleFolderMeta).id === "string"),
    );
  } catch {
    return [];
  }
}

/** Unpack `.adam` zip bytes into a project snapshot (+ optional audio assets). */
export function deserializeAdamPackage(bytes: Uint8Array): AdamPackage {
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(bytes);
  } catch {
    throw new Error("Not a valid .adam zip package");
  }

  const projectEntry = entries[ADAM_PROJECT_JSON_PATH];
  if (!projectEntry) {
    throw new Error(`.adam package is missing ${ADAM_PROJECT_JSON_PATH}`);
  }

  let documentJson: unknown;
  try {
    documentJson = JSON.parse(strFromU8(projectEntry));
  } catch {
    throw new Error(`Corrupt ${ADAM_PROJECT_JSON_PATH}`);
  }

  const snapshot = parseAdamProjectDocumentJson(documentJson);
  const project = buildLabProject(snapshot);

  const metadataEntry = entries[ADAM_METADATA_JSON_PATH];
  let metadata: AdamMetadataJson;
  if (metadataEntry) {
    try {
      metadata = JSON.parse(strFromU8(metadataEntry)) as AdamMetadataJson;
    } catch {
      metadata = {
        format: ADAM_FORMAT,
        version: ADAM_PACKAGE_VERSION,
        savedAt: new Date().toISOString(),
        generator: "adam",
      };
    }
  } else {
    metadata = {
      format: ADAM_FORMAT,
      version: ADAM_PACKAGE_VERSION,
      savedAt: new Date().toISOString(),
      generator: "adam",
    };
  }

  return {
    snapshot,
    metadata,
    project,
    samples: extractSamples(entries),
    folders: extractFolders(entries),
  };
}

/**
 * Detect whether bytes look like a zip (PK\x03\x04 or empty zip PK\x05\x06).
 * Used so import can fall back to legacy JSON.
 */
export function looksLikeZip(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 4
    && bytes[0] === 0x50
    && bytes[1] === 0x4b
    && (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07)
  );
}
